import { add } from 'date-fns';
import Decimal from 'decimal.js-light';

import { ClientInputError } from '@/lib/errors/client';

import { getTokenAmount } from '@/components/TransactionModule/MarketMode/utils';

import type { AdvancedOrderType } from '@/constants/advancedOrder';

import type { LimitOrderExpiration } from '@/store/limitOrder';

import type {
  DefinitiveLimitOrderBuildResponse,
  DefinitiveLimitOrderPreviewResponse,
} from '@/utils/0x.types';
import {
  fetchLimitOrderBuild,
  LimitOrderBuildHttpError,
  toLimitOrderBuildBody,
  type LimitOrderBuildBody,
} from '@/utils/limitOrderBuild';

type QuoteToken = { address: string; decimals: number };

/** Everything that identifies one Definitive estimate; doubles as the query key payload. */
export type AdvancedOrderQuoteInputs = {
  chainId: number;
  /** Undefined quotes a wallet-less preview that carries no signing payload. */
  maker: string | undefined;
  sellToken: QuoteToken;
  buyToken: QuoteToken;
  /** Sell amount in base units. */
  sellAmount: string;
  /** Gross buy amount in base units (sell × rate). It fixes the limit price Definitive executes at. */
  buyAmount: string;
  expiration: LimitOrderExpiration;
  /** Epoch ms; only read when `expiration` is `custom`. */
  customExpiration: number | undefined;
  orderType: AdvancedOrderType;
  triggerCrossPrice: string | undefined;
};

export type AdvancedOrderEstimate = {
  /** The exact body sent to build-order, so submit can prove a cached payload still matches the form. */
  request: LimitOrderBuildBody;
  build: DefinitiveLimitOrderBuildResponse | DefinitiveLimitOrderPreviewResponse;
};

/** A signable build whose server quote context is still alive. */
export type ReusableLimitOrderBuild = {
  request: LimitOrderBuildBody;
  response: DefinitiveLimitOrderBuildResponse;
};

/** Reuse stops this long before the server context TTL so a payload never lands on an evicted context. */
const REUSE_SAFETY_MARGIN_MS = 15_000;

export function advancedOrderQuoteKey(inputs: AdvancedOrderQuoteInputs | null) {
  return ['advanced-order-quote', inputs] as const;
}

/** Definitive shares 5 rps per endpoint across every Matcha user; a 429 clears within one second. */
const RATE_LIMIT_RETRY_DELAY_MS = 1000;
const RATE_LIMIT_MAX_RETRIES = 2;

/** Only a rate limit is worth retrying; every other build failure is deterministic for these inputs. */
export const advancedOrderQuoteRetryOptions = {
  retry: (failureCount: number, error: Error) =>
    error instanceof LimitOrderBuildHttpError &&
    error.status === 429 &&
    failureCount < RATE_LIMIT_MAX_RETRIES,
  retryDelay: RATE_LIMIT_RETRY_DELAY_MS,
};

export function isRateLimitedQuoteError(error: unknown): boolean {
  return error instanceof LimitOrderBuildHttpError && error.status === 429;
}

/** Resolves the store's expiry setting to a concrete date; the relative options anchor on `now`. */
export function getAdvancedOrderExpiry(
  expiration: LimitOrderExpiration,
  customExpiration: Date | number | undefined,
  now: Date = new Date(),
): Date {
  // 'custom' with no picked date would otherwise parse to NaN and POST an Invalid Date expiry.
  const expiry =
    expiration === 'custom'
      ? customExpiration === undefined
        ? undefined
        : new Date(customExpiration)
      : add(now, { minutes: parseInt(expiration, 10) });
  if (!expiry || Number.isNaN(expiry.getTime())) {
    throw new ClientInputError('submitLimitOrder: invalid expiration');
  }
  return expiry;
}

/** Gross buy amount in base units for a sell amount and sell-side rate; the order's executable terms. */
export function getAdvancedGrossBuyAmount(
  sellDecimal: string,
  rateSell: string,
  buyDecimals: number,
): string | undefined {
  // toFixed keeps very large products (≥1e21) out of exponent notation, which parseUnits rejects.
  return getTokenAmount(new Decimal(sellDecimal).mul(rateSell).toFixed(), buyDecimals);
}

export async function fetchAdvancedOrderEstimate(
  inputs: AdvancedOrderQuoteInputs,
): Promise<AdvancedOrderEstimate> {
  const request = toLimitOrderBuildBody({
    chainId: inputs.chainId,
    maker: inputs.maker,
    makerAsset: inputs.sellToken.address,
    takerAsset: inputs.buyToken.address,
    makingAmount: inputs.sellAmount,
    takingAmount: inputs.buyAmount,
    makerDecimals: inputs.sellToken.decimals,
    takerDecimals: inputs.buyToken.decimals,
    orderType: inputs.orderType,
    triggerCrossPrice: inputs.triggerCrossPrice,
    expiry: getAdvancedOrderExpiry(inputs.expiration, inputs.customExpiration),
  });
  const build = await fetchLimitOrderBuild(request);
  if (build.provider !== 'definitive') {
    throw new Error('Advanced order estimates are only available from Definitive');
  }
  return { request, build };
}

export function getReusableSignableBuild(
  estimate: AdvancedOrderEstimate | undefined,
  now: number = Date.now(),
): ReusableLimitOrderBuild | undefined {
  if (!estimate || !estimate.build.signable) return undefined;
  if (now >= estimate.build.expiresAt - REUSE_SAFETY_MARGIN_MS) return undefined;
  return { request: estimate.request, response: estimate.build };
}
